// ============================================================================
// The finance view — reads only. Run B Phase 4, 2026-08-18.
//
// Run A found things and fixed none of them, on purpose. This screen is where
// they become visible instead of living in a report nobody reopens. It renders
// four database views and it has NO WRITE PATH TO ANY AMOUNT — historic
// reconciliation is suspended, so a screen that could edit one of these numbers
// would be a screen that could quietly restart it.
//
// What finance CAN do here is say what is correct, in words, against the record.
// That note is the deliverable, not a corrected figure.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { addProjectNote } from '../hooks/use-project-data';

export interface MintMismatch {
  payment_id: string;
  project_id: string;
  provider_id: string | null;
  payment_amount: number | null;
  project_pay_amount: number | null;
  delta: number | null;
  direction: string | null;
  status: string | null;
  /**
   * TEXT, not boolean — it holds 'Y' / 'N'. Every payment on this view carries
   * 'N'. Written as a comment because I got this wrong first time: a JavaScript
   * truthiness check on it renders 'N' as SYNCED, i.e. the screen says the
   * opposite of the truth about a payment. Compare it explicitly, always.
   */
  qbo_synced: string | null;
  facility_name: string | null;
  special_pay: boolean | null;
  service_line_count: number | null;
}


export interface DateContradiction {
  project_id: string;
  facility_name: string | null;
  stage: string | null;
  service_type_raw: string | null;
  earliest_claimed_date: string | null;
  contract_start: string | null;
  days_before_service_started: number | null;
  bill_amount: number | null;
  pay_amount: number | null;
}

export interface CleanupRow {
  project_id: string;
  facility_name: string | null;
  account_name: string | null;
  stage: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  best_date: string | null;
  created_by_eid: string | null;
  flags: string[] | null;
}

interface ViewState<T> {
  rows: T[];
  isLoading: boolean;
  error: string | null;
}

function useView<T>(view: string, columns: string, tick: number): ViewState<T> {
  const [state, setState] = useState<ViewState<T>>({ rows: [], isLoading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.from(view).select(columns);
        if (cancelled) return;
        if (error) throw error;
        setState({ rows: (data ?? []) as unknown as T[], isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ rows: [], isLoading: false, error: errorMessage(err, `Failed to load ${view}.`) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, columns, tick]);
  return state;
}

export function useFinanceFindings() {
  const [tick, setTick] = useState(0);

  const mismatches = useView<MintMismatch>(
    'v_project_mint_amount_mismatches',
    'payment_id, project_id, provider_id, payment_amount, project_pay_amount, delta, direction, status, qbo_synced, facility_name, special_pay, service_line_count',
    tick,
  );
  const dates = useView<DateContradiction>(
    'v_project_dates_before_service_started',
    'project_id, facility_name, stage, service_type_raw, earliest_claimed_date, contract_start, days_before_service_started, bill_amount, pay_amount',
    tick,
  );
  const cleanup = useView<CleanupRow>(
    'v_project_cleanup_queue',
    'project_id, facility_name, account_name, stage, bill_amount, pay_amount, best_date, created_by_eid, flags',
    tick,
  );

  return useMemo(
    () => ({ mismatches, dates, cleanup, refetch: () => setTick((n) => n + 1) }),
    [mismatches, dates, cleanup],
  );
}

/**
 * Say what is correct, against the record, in words.
 *
 * For anything with a live project the note lands on that project's append-only
 * activity log, where the next person to open the card will see it. A finding
 * with no project goes to the app's own feedback channel instead of inventing a
 * table.
 */
export async function recordFinanceNote(
  target: { projectId: string | null; label: string },
  note: string,
  actorEid: string,
): Promise<void> {
  const trimmed = note.trim();
  if (!trimmed) throw new Error('Write what is correct — a blank note records nothing.');
  if (target.projectId) {
    await addProjectNote(target.projectId, `Finance — ${target.label}: ${trimmed}`, actorEid);
    return;
  }
  const { error } = await supabase.rpc('submit_feedback', {
    p_type: 'issue',
    p_source: 'project_findings',
    p_title: `Finance note — ${target.label}`,
    p_description: trimmed,
    p_module: 'project-work',
    p_url_path: '/project-work/findings',
    p_chat_excerpt: null,
    p_user_agent: null,
  });
  if (error) throw error;
}
